import type { User, AuthTokens, LoginCredentials, RegisterCredentials } from '../types';
import { API_BASE } from './api';

const TOKEN_STORAGE_KEY = 'auth_tokens';

class AuthService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get stored tokens from localStorage
   */
  getStoredTokens(): AuthTokens | null {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  }

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Get the access token for API requests
   */
  getAccessToken(): string | null {
    const tokens = this.getStoredTokens();
    return tokens?.access_token ?? null;
  }

  /**
   * Register a new user
   */
  async register(credentials: RegisterCredentials): Promise<AuthTokens> {
    const response = await fetch(`${this.baseUrl}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
      throw new Error(error.detail || 'Registration failed');
    }

    const tokens: AuthTokens = await response.json();
    this.storeTokens(tokens);
    return tokens;
  }

  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Invalid credentials' }));
      throw new Error(error.detail || 'Invalid credentials');
    }

    const tokens: AuthTokens = await response.json();
    this.storeTokens(tokens);
    return tokens;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<AuthTokens | null> {
    const currentTokens = this.getStoredTokens();
    if (!currentTokens?.refresh_token) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: currentTokens.refresh_token }),
      });

      if (!response.ok) {
        this.clearTokens();
        return null;
      }

      const tokens: AuthTokens = await response.json();
      this.storeTokens(tokens);
      return tokens;
    } catch {
      this.clearTokens();
      return null;
    }
  }

  /**
   * Get the current user's profile
   */
  async getCurrentUser(): Promise<User | null> {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Try to refresh token
          const refreshed = await this.refreshToken();
          if (refreshed) {
            return this.getCurrentUser();
          }
        }
        return null;
      }

      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    const token = this.getAccessToken();
    if (token) {
      try {
        await fetch(`${this.baseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch {
        // Ignore logout errors, clear tokens anyway
      }
    }
    this.clearTokens();
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }
}

export const authService = new AuthService();

// One refresh shared by every request that hits a 401 at the same moment.
let refreshInFlight: Promise<AuthTokens | null> | null = null;

/**
 * fetch() for our API that survives an expired access token. If a request that
 * carried a token comes back 401, refresh once and retry it with the new token.
 * If the refresh fails (the refresh token expired too), the tokens are cleared
 * and the page reloads, which lands on the login screen instead of an error.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 401 || !new Headers(init.headers).has('Authorization')) {
    return response;
  }

  refreshInFlight ??= authService.refreshToken().finally(() => { refreshInFlight = null; });
  const tokens = await refreshInFlight;
  if (!tokens) {
    window.location.reload();
    return response;
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${tokens.access_token}`);
  return fetch(input, { ...init, headers });
}
